import styled from "styled-components";
import { Row, Col, Divider, Skeleton, Grid } from "antd";
import { useDispatch, useSelector } from "react-redux";

import { RATypography, RAButtonPrimary, RAButtonSecondary, RATabs } from "@/components/customs";
import { FLIGHT_REQUEST_OPTIONS } from "@/config/constants";
import { FormRequestSummaryLegItem } from "../molecules";
import { SvgCheck } from "@/assets/icons";
import { lightTheme } from "@/themes/ThemeConfig";
import { selectAuthState, setOpenAuthModal } from "@/stores/slices/authSlice";
import { additionalTitle } from "@/utils/index";

const { useBreakpoint } = Grid;

export function FormRequestFlightSummary({ data = {}, onBack, onSubmitRequest, loading, formatTimezone = true }) {
	const dispatch = useDispatch();
	const screens = useBreakpoint();

	const { user } = useSelector(selectAuthState);

	const { type, note = "", legs = [], additional_info = {}, aircraft_categories = [] } = data;

	const _renderLegByType = () => {
		if (type === FLIGHT_REQUEST_OPTIONS.ONE_WAY) {
			return (
				<FormRequestSummaryLegItem
					key="flight-request-item-one-way"
					legs={legs}
					categories={aircraft_categories}
					formValues={legs[0]}
					formatTimezone={formatTimezone}
				/>
			);
		}
		if (type === FLIGHT_REQUEST_OPTIONS.ROUND_TRIP) {
			const tabRenderer = [
				{
					key: "DEPARTURE",
					label: "Departure",
					render: (
						<FormRequestSummaryLegItem
							key={`flight-request-item-0`}
							legs={legs}
							categories={aircraft_categories}
							formValues={legs[0]}
							formatTimezone={formatTimezone}
						/>
					),
				},
				{
					key: "ARRIVAL",
					label: "Arrival",
					render: (
						<FormRequestSummaryLegItem
							key={`flight-request-item-1`}
							legs={legs}
							categories={aircraft_categories}
							formValues={legs[1]}
							formatTimezone={formatTimezone}
						/>
					),
				},
			];
			return <RATabs tabRenderer={tabRenderer} />;
		}
		if (type === FLIGHT_REQUEST_OPTIONS.MULTI_LEG) {
			const tabRenderer = legs?.map((item, index) => {
				return {
					key: `LEG ${index + 1}`,
					label: `Leg ${index + 1}`,
					render: (
						<FormRequestSummaryLegItem
							key={`flight-request-item-${index}`}
							formValues={item}
							legs={legs}
							categories={aircraft_categories}
							formatTimezone={formatTimezone}
						/>
					),
				};
			});
			return <RATabs tabRenderer={tabRenderer} />;
		}
		return <Skeleton style={{ margin: "12px 0" }} />;
	};

	const _renderAdditionalInfo = () => {
		return (
			<WrapperAdditionalRequest>
				<RATypography variant="sub-heading4" color="#121216">
					Additional Requests
				</RATypography>
				{!!note && (
					<RATypography variant="body3" color="#65686F">
						{note}
					</RATypography>
				)}
				<WrapperRequestServices>
					{Object.keys(additional_info).map((v) => {
						if (!!additional_info[v]) {
							return (
								<Row gutter={[12, 12]} align="middle" key={v}>
									<Col>
										<SvgCheck color={lightTheme.color_status_success_500} />
									</Col>
									<Col>
										<RATypography variant="sub-heading4" color="#222222">
											{additionalTitle[v]}
										</RATypography>
									</Col>
								</Row>
							);
						}
					})}
				</WrapperRequestServices>
			</WrapperAdditionalRequest>
		);
	};

	const handleOpenAuthModal = () => {
		if (!user) {
			dispatch(setOpenAuthModal({ title: "Log In To Confirm" }));
		}
	};

	return (
		<ContainerRequestFlight>
			<WrapperContent>
				<RATypography variant={screens.xs ? "h4" : "h3"}>Summary</RATypography>
			</WrapperContent>
			<Divider />
			{_renderLegByType()}
			<Divider />
			{_renderAdditionalInfo()}
			<Row gutter={[20, 12]} justify="center" align="middle" style={{ marginTop: "30px" }}>
				<ButtonCol xs={12}>
					<RAButtonSecondary title="BACK" onClick={() => onBack()} width="100%" />
				</ButtonCol>
				<ButtonCol xs={12}>
					{user ? (
						<RAButtonPrimary
							title="SUBMIT"
							onClick={onSubmitRequest}
							width="100%"
							loading={loading}
							data-testid="FlightRequestSummary_submitBtn"
						/>
					) : (
						<RAButtonPrimary
							title="LOG IN"
							onClick={handleOpenAuthModal}
							width="100%"
							data-testid="FlightRequestSummary_loginBtn"
						/>
					)}
				</ButtonCol>
			</Row>
		</ContainerRequestFlight>
	);
}

const ContainerRequestFlight = styled.div`
	width: "100%";
`;
const WrapperContent = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;
const WrapperAdditionalRequest = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;
const WrapperRequestServices = styled.div`
	margin-top: 12px;
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px 24px;
`;
const ButtonCol = styled(Col)`
	max-width: 180px;
	width: 100%;
`;
